import {
  Activity, BarChart3, Blocks, Bot, Building2, ChevronDown, CircleGauge, ContactRound, FileUp, Filter, Gauge, TrendingUp,
  Inbox, LayoutDashboard, LifeBuoy, ListTodo, Menu, MessageSquareText, Plus, Search, Settings, Sparkles, Target,
  Workflow, X, Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EMBEDDED, api } from "../lib/api";
import { BRAND } from "../brand";

type Actor = { email: string; role: string };
type TemplateWorkspace = { orgId: number | null; name: string; active: boolean };

/** Inside the admin template: the template organizations this user may open, one Nexus workspace each. */
function WorkspaceSwitcher() {
  const workspaces = useQuery({ queryKey: ["template-workspaces"], queryFn: () => api<{ items: TemplateWorkspace[] }>("/api/workspaces") });
  const items = workspaces.data?.items ?? [];
  if (items.length < 2) return null;
  const choose = (value: string) => {
    document.cookie = `nexus_org=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
    window.location.reload();
  };
  return <label className="workspace-switcher"><span>Workspace</span><select value={String(items.find((item) => item.active)?.orgId ?? "default")} onChange={(event) => choose(event.target.value)}>{items.map((item) => <option key={String(item.orgId)} value={item.orgId === null ? "default" : String(item.orgId)}>{item.name}</option>)}</select></label>;
}

const groups: Array<{ label: string; items: Array<[string, string, LucideIcon]> }> = [
  { label: "Workspace", items: [
    ["/", "Overview", LayoutDashboard],
    ["/contacts", "Contacts", ContactRound],
    ["/organizations", "Organizations", Building2],
  ] },
  { label: "Revenue", items: [
    ["/deals", "Deals & Pipelines", Target],
    ["/activities", "Activities", Activity],
    ["/tasks", "Tasks", ListTodo],
  ] },
  { label: "Engagement", items: [
    ["/segments", "Segments", CircleGauge],
    ["/campaigns", "Campaigns", Sparkles],
    ["/journeys", "Journeys", Workflow],
    ["/inbox", "Inbox", Inbox],
    ["/automations", "Automations", Bot],
  ] },
  { label: "Intelligence", items: [
    ["/growth-advisor", "Growth Advisor", TrendingUp],
    ["/salon-funnel", "Salon Funnel", Filter],
    ["/channel-funnel", "Channel Funnel", BarChart3],
    ["/reports", "Reports", BarChart3],
    ["/operations", "Data Operations", Gauge],
    ["/imports", "Imports", FileUp],
    ["/object-studio", "Object Studio", Blocks],
  ] },
];

export function Shell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); setPaletteOpen((value) => !value); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  const session = useQuery({ queryKey: ["session"], queryFn: () => api<{ actor?: Actor }>("/api/auth/me"), retry: false });
  const actor = session.data?.actor;
  const logout = async () => {
    if (EMBEDDED) return window.location.assign("/admin/logout");
    await api("/api/auth/logout", { method: "POST" });
    queryClient.clear();
    navigate("/login");
  };
  return <div className="app-shell">
    <aside className={mobileOpen ? "sidebar open" : "sidebar"}>
      <div className="brand"><div className="brand-mark"><Zap size={19} fill="currentColor" /></div><div><strong>{BRAND.short}</strong><span>CRM</span></div><button className="mobile-close" onClick={() => setMobileOpen(false)}><X /></button></div>
      {EMBEDDED && <WorkspaceSwitcher />}<button className="quick-create" onClick={() => navigate("/contacts?create=1")}><Plus size={17} /> Quick create <kbd>Q</kbd></button>
      <nav>{groups.map((group) => <div className="nav-group" key={group.label}><div className="nav-label">{group.label}</div>{group.items.map(([to, label, Icon]) => <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}><Icon size={17} /><span>{label}</span>{label === "Inbox" && <i>3</i>}</NavLink>)}</div>)}</nav>
      <div className="sidebar-bottom"><NavLink to="/settings" className={({ isActive }) => isActive ? "nav-item active" : "nav-item"}><Settings size={17} /><span>Settings</span></NavLink><a className="nav-item" href="https://github.com/AIKitLLC/nexus-crm" target="_blank" rel="noreferrer"><LifeBuoy size={17} /><span>Documentation</span></a><button className="profile-chip" onClick={logout}><div className="avatar">{(actor?.email ?? "?").slice(0, 2).toUpperCase()}</div><div><strong>{actor?.email ?? "Signed in"}</strong><span>{actor?.role ?? ""}</span></div><ChevronDown size={15} /></button></div>
    </aside>
    {mobileOpen && <button className="sidebar-scrim" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
    <main className="main-area">
      <div className="topbar"><button className="mobile-menu" aria-label="Open navigation" onClick={() => setMobileOpen(true)}><Menu /></button><button className="global-search" onClick={() => setPaletteOpen(true)}><Search size={17} /><span>Search anything…</span><kbd>⌘ K</kbd></button><div className="topbar-right"><span className="environment"><i /> Production</span><button className="icon-button" aria-label="Notifications"><MessageSquareText size={18} /></button></div></div>
      <div className="page-container"><Outlet /></div>
    </main>
    {paletteOpen && <div className="command-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setPaletteOpen(false)}><div className="command-palette"><div className="command-input"><Search size={19} /><input autoFocus placeholder="Jump to a page or record…" /><kbd>ESC</kbd></div><div className="command-section"><span>Navigate</span>{groups.flatMap((group) => group.items).slice(0, 8).map(([to, label, Icon]) => <button key={to} onClick={() => { navigate(to); setPaletteOpen(false); }}><Icon size={17} />{label}</button>)}</div></div></div>}
  </div>;
}
